import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import BusinessEmailAddressInput from '../form-inputs/business-email-address-input/BusinessEmailAddressInput';
import BusinessPhoneNumberInput from '../form-inputs/business-phone-number-input/BusinessPhoneNumberInput';
import AccountCircleIcon from '../../../assets/svg/account_circle.svg?react';
import BrushIcon from '../../../assets/svg/brush.svg?react';

import CircularProgress from '@mui/material/CircularProgress';
import { useEmergencyContactCardForm } from './useEmergencyContactCardForm';
import styles from './EmergencyContactCard.module.scss';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import Tooltip from '@mui/material/Tooltip';
// import { SocialLink } from '../social-media-form/SocialMediaForm';
import { Employee } from '../../models/employee.model';

type EmergencyContactCardProps = {
  simplified?: boolean;
  employee: Partial<Employee>;
};

export default function EmergencyContactCard({ employee }: EmergencyContactCardProps) {
  const { t } = useTranslation();
  const isLoading = false;
  const {
    formData,
    handleChange,
    saveChanges,
    isEdited,
    toggleEditing,
    formRef,
    showModal,
    confirmExit,
    cancelChanges,
  } = useEmergencyContactCardForm(employee);

  const relationshipOptions = ['Friend', 'Guardian', 'Parent', 'Partner', 'Sibling'];

  return (
    <Grid component='div' container className='inputs-editable'>
      <Grid component='div' size={{ xs: 12 }}>
        <Card className={`${styles.card} ${isEdited ? styles.card_border : ''}`} ref={formRef}>
          <CardContent className={isLoading ? styles.card_content_min_height : ''}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <>
                <Grid
                  component='div'
                  container
                  style={{ alignItems: 'center', flexWrap: 'nowrap', padding: '8px' }}
                >
                  <Grid
                    component='div'
                    size={{ xs: 11 }}
                    container
                    style={{ alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}
                  >
                    <Grid
                      component='div'
                      sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                      <AccountCircleIcon />
                    </Grid>
                    <Grid component='div'>
                      <Typography variant='h6'>Emergency Contact</Typography>
                      {/* TODO : Add translation, for some reason didn't wanna work will look later */}
                    </Grid>
                  </Grid>
                  <Grid
                    component='div'
                    size={{ xs: 1 }}
                    container
                    style={{ justifyContent: 'flex-end' }}
                  >
                    {!isEdited ? (
                      <Tooltip title={t('buttons.edit')} placement='top'>
                        <IconButton
                          id='emergencyContactCardEditIconBtn'
                          onClick={toggleEditing}
                          sx={{ flexShrink: 0 }}
                        >
                          <BrushIcon id='emergencyContactCardEditIconBtnBrushIcon' />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Button
                          id='emergencyContactCardCancelBtn'
                          type='button'
                          className='btn-default btn-action-basic btn-cancel'
                          role='button'
                          variant='contained'
                          size='large'
                          onClick={cancelChanges}
                        >
                          {t('buttons.cancel')}
                        </Button>
                        <Button
                          id='emergencyContactCardSaveBtn'
                          type='submit'
                          className='btn-default btn-action-basic'
                          role='button'
                          disabled={!isEdited}
                          variant='contained'
                          size='large'
                          onClick={saveChanges}
                        >
                          {t('buttons.save')}
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>

                <Grid component='div' container spacing={2}>
                  <Grid component='div' size={{ xs: 12 }}>
                    <Grid component='div' container spacing={2}>
                      <Grid component='div' size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          value={formData.full_name}
                          variant={'outlined'}
                          label={t('labels.fullName')}
                          onChange={(e) => {
                            handleChange('full_name')(e.target.value);
                          }}
                          disabled={!isEdited}
                        />
                      </Grid>
                      <Grid component='div' size={{ xs: 12, sm: 6 }}>
                        {' '}
                        <BusinessEmailAddressInput
                          value={formData.email_address || ''}
                          onChange={handleChange('email_address')}
                          disabled={!isEdited}
                          label={t('labels.emailAddress')}
                        />
                      </Grid>
                      <Grid component='div' size={{ xs: 12, sm: 6 }}>
                        {' '}
                        <BusinessPhoneNumberInput
                          value={formData.phone_number}
                          onChange={handleChange('phone_number')}
                          disabled={!isEdited}
                          label={t('labels.phoneNumber')}
                        />
                      </Grid>

                      <Grid component='div' size={{ xs: 12, sm: 6 }}>
                        {' '}
                        <Select
                          id='emergencyContactCardChangeRelationshipSelect'
                          fullWidth
                          value={formData.relationship || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleChange('relationship')(e.target.value);
                          }}
                          disabled={!isEdited}
                          displayEmpty
                          inputProps={{ 'aria-label': t('labels.relationship') }}
                        >
                          <MenuItem
                            id='emergencyContactCardSelectRelationshipMenuItem'
                            value=''
                            disabled
                          >
                            Select relationship
                          </MenuItem>
                          {relationshipOptions.map((option) => (
                            <MenuItem
                              id={`emergencyContactCardRelationshipOptionMenuItem_${option}`}
                              key={option}
                              value={option}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {
                  <Grid
                    component='div'
                    container
                    className={styles.container_margin_top}
                    style={{ alignItems: 'center' }}
                  >
                    <Grid component='div'>
                      {/* <Tooltip title={t('businessProfile.addSocials')} placement='top'>
                        <IconButton
                          onClick={toogleEditingSocialModal}
                          className={styles.add_social_button}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Tooltip> */}
                    </Grid>
                  </Grid>
                }
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
      {/* <AddSocialsModal
        open={showAddSocialModal}
        onClose={saveChangesSocialForm}
        onConfirm={toogleEditingSocialModal}
      /> */}
    </Grid>
  );
}
